import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
const KEY = 'sd.theme';

function readPreference(): ThemePreference {
  try {
    const value = globalThis.localStorage?.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): 'light' | 'dark' {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Applies `html[data-theme]` like the design and follows the OS while on "시스템". Per-viewer only. */
export function useTheme() {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);

  useEffect(() => {
    const apply = () => {
      const next = preference === 'system' ? systemTheme() : preference;
      document.documentElement.dataset.theme = next;
    };
    apply();
    if (preference !== 'system') return undefined;
    const query = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    query?.addEventListener('change', apply);
    return () => query?.removeEventListener('change', apply);
  }, [preference]);

  const setPreference = useCallback((value: ThemePreference) => {
    try {
      if (value === 'system') globalThis.localStorage?.removeItem(KEY);
      else globalThis.localStorage?.setItem(KEY, value);
    } catch {
      // storage unavailable (private window, blocked site data): keep in memory only
    }
    setPreferenceState(value);
  }, []);

  return { preference, setPreference };
}
